import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';

const CoinContext = createContext();

export const CoinProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [coins, setCoins] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCoins = async () => {
      if (!currentUser) {
        setCoins(0);
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          setCoins(userData.coins || 0);
        } else {
          // อย่าสร้าง user document อัตโนมัติที่นี่
          // ให้ผู้ใช้เลือก role ก่อนที่หน้า register
          // จะสร้าง user document เมื่อเลือก role แล้ว
          setCoins(0);
        }
      } catch (err) {
        console.error("Error fetching coins:", err);
        setCoins(0);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchCoins();
    }
  }, [currentUser, authLoading]);

  const addCoins = async (amount) => {
    if (!currentUser) {
      throw new Error('กรุณาเข้าสู่ระบบ');
    }

    if (!currentUser.uid) {
      throw new Error('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      const currentCoins = coins || 0;
      const newCoins = currentCoins + amount;
      
      if (userSnap.exists()) {
        // User document exists, update it
        await updateDoc(userRef, {
          coins: newCoins,
          updatedAt: new Date()
        });
      } else {
        // User document doesn't exist, create it
        const userData = {
          uid: currentUser.uid,
          coins: newCoins,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Add optional fields if they exist
        if (currentUser.email) {
          userData.email = currentUser.email;
        }
        if (currentUser.displayName) {
          userData.displayName = currentUser.displayName;
        }
        
        await setDoc(userRef, userData);
      }
      
      setCoins(newCoins);
    } catch (error) {
      console.error('Error adding coins:', error);
      // Provide more specific error message
      if (error.code === 'permission-denied') {
        throw new Error('ไม่มีสิทธิ์ในการอัปเดตข้อมูล กรุณาติดต่อแอดมิน');
      } else if (error.code === 'unavailable') {
        throw new Error('ไม่สามารถเชื่อมต่อกับฐานข้อมูลได้ กรุณาลองใหม่');
      } else {
        throw new Error(`เกิดข้อผิดพลาด: ${error.message || 'ไม่ทราบสาเหตุ'}`);
      }
    }
  };

  const deductCoins = async (amount) => {
    if (!currentUser) {
      throw new Error('กรุณาเข้าสู่ระบบ');
    }

    if (!currentUser.uid) {
      throw new Error('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      const currentCoins = coins || 0;
      const newCoins = Math.max(0, currentCoins - amount);
      
      if (userSnap.exists()) {
        // User document exists, update it
        await updateDoc(userRef, {
          coins: newCoins,
          updatedAt: new Date()
        });
      } else {
        // User document doesn't exist, create it
        const userData = {
          uid: currentUser.uid,
          coins: newCoins,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Add optional fields if they exist
        if (currentUser.email) {
          userData.email = currentUser.email;
        }
        if (currentUser.displayName) {
          userData.displayName = currentUser.displayName;
        }
        
        await setDoc(userRef, userData);
      }
      
      setCoins(newCoins);
    } catch (error) {
      console.error('Error deducting coins:', error);
      // Provide more specific error message
      if (error.code === 'permission-denied') {
        throw new Error('ไม่มีสิทธิ์ในการอัปเดตข้อมูล กรุณาติดต่อแอดมิน');
      } else if (error.code === 'unavailable') {
        throw new Error('ไม่สามารถเชื่อมต่อกับฐานข้อมูลได้ กรุณาลองใหม่');
      } else {
        throw new Error(`เกิดข้อผิดพลาด: ${error.message || 'ไม่ทราบสาเหตุ'}`);
      }
    }
  };

  const value = {
    coins,
    loading,
    addCoins,
    deductCoins,
  };

  return (
    <CoinContext.Provider value={value}>
      {children}
    </CoinContext.Provider>
  );
};

export const useCoins = () => {
  return useContext(CoinContext);
};